import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Plus, Calendar, Trash2, Edit2, Gift, Heart, Users, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useBadgeAwarder } from '@/hooks/useBadgeAwarder';
import { useJacKeyNudgeContext } from '@/contexts/JacKeyNudgeContext';
import { importantDateSchema } from '@/lib/validations';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { DateType, ImportantDate } from '@/types/gamification';
import type { Database } from '@/integrations/supabase/types';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const datesCopy = {
  en: { title: 'Important Dates', desc: 'Track birthdays, anniversaries, and special dates for your loved ones', addDate: 'Add Date', editDate: 'Edit Date', addImportant: 'Add Important Date', dialogDesc: 'Save important dates to never miss a chance to express love', recipientName: 'Recipient Name', recipientPlaceholder: 'e.g., Mom, John, Sarah', dateType: 'Date Type', date: 'Date', month: 'Month', day: 'Day', year: 'Year', notes: 'Notes (optional)', notesPlaceholder: 'Any special notes about this date...', cancel: 'Cancel', saving: 'Saving...', update: 'Update', loading: 'Loading dates...', empty: 'No important dates saved yet', emptyHint: 'Add birthdays and anniversaries to earn special badges!', deleteTitle: 'Delete this date?', deleteBtn: 'Delete', birthday: 'Birthday', relAnniv: 'Relationship Anniversary', friendAnniv: 'Friendship Anniversary', otherDate: 'Other Special Date', months: ['January','February','March','April','May','June','July','August','September','October','November','December'] },
  es: { title: 'Fechas Importantes', desc: 'Registra cumpleaños, aniversarios y fechas especiales de tus seres queridos', addDate: 'Agregar Fecha', editDate: 'Editar Fecha', addImportant: 'Agregar Fecha Importante', dialogDesc: 'Guarda fechas importantes para no perder la oportunidad de expresar amor', recipientName: 'Nombre del Destinatario', recipientPlaceholder: 'ej., Mamá, Juan, Sara', dateType: 'Tipo de Fecha', date: 'Fecha', month: 'Mes', day: 'Día', year: 'Año', notes: 'Notas (opcional)', notesPlaceholder: 'Notas especiales sobre esta fecha...', cancel: 'Cancelar', saving: 'Guardando...', update: 'Actualizar', loading: 'Cargando fechas...', empty: 'No hay fechas importantes guardadas', emptyHint: '¡Agrega cumpleaños y aniversarios para ganar insignias especiales!', deleteTitle: '¿Eliminar esta fecha?', deleteBtn: 'Eliminar', birthday: 'Cumpleaños', relAnniv: 'Aniversario de Relación', friendAnniv: 'Aniversario de Amistad', otherDate: 'Otra Fecha Especial', months: ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'] },
  fr: { title: 'Dates Importantes', desc: 'Suivez les anniversaires et dates spéciales de vos proches', addDate: 'Ajouter une Date', editDate: 'Modifier la Date', addImportant: 'Ajouter une Date Importante', dialogDesc: 'Enregistrez des dates importantes pour ne jamais manquer une occasion d\'exprimer votre amour', recipientName: 'Nom du Destinataire', recipientPlaceholder: 'ex., Maman, Jean, Sarah', dateType: 'Type de Date', date: 'Date', month: 'Mois', day: 'Jour', year: 'Année', notes: 'Notes (facultatif)', notesPlaceholder: 'Notes spéciales sur cette date...', cancel: 'Annuler', saving: 'Enregistrement...', update: 'Mettre à jour', loading: 'Chargement des dates...', empty: 'Aucune date importante enregistrée', emptyHint: 'Ajoutez des anniversaires pour gagner des badges spéciaux !', deleteTitle: 'Supprimer cette date ?', deleteBtn: 'Supprimer', birthday: 'Anniversaire', relAnniv: 'Anniversaire de Relation', friendAnniv: 'Anniversaire d\'Amitié', otherDate: 'Autre Date Spéciale', months: ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'] },
  pt: { title: 'Datas Importantes', desc: 'Acompanhe aniversários e datas especiais dos seus entes queridos', addDate: 'Adicionar Data', editDate: 'Editar Data', addImportant: 'Adicionar Data Importante', dialogDesc: 'Salve datas importantes para nunca perder a chance de expressar amor', recipientName: 'Nome do Destinatário', recipientPlaceholder: 'ex., Mãe, João, Sara', dateType: 'Tipo de Data', date: 'Data', month: 'Mês', day: 'Dia', year: 'Ano', notes: 'Notas (opcional)', notesPlaceholder: 'Notas especiais sobre esta data...', cancel: 'Cancelar', saving: 'Salvando...', update: 'Atualizar', loading: 'Carregando datas...', empty: 'Nenhuma data importante salva ainda', emptyHint: 'Adicione aniversários para ganhar badges especiais!', deleteTitle: 'Excluir esta data?', deleteBtn: 'Excluir', birthday: 'Aniversário', relAnniv: 'Aniversário de Relacionamento', friendAnniv: 'Aniversário de Amizade', otherDate: 'Outra Data Especial', months: ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'] },
  ar: { title: 'تواريخ مهمة', desc: 'تتبع أعياد الميلاد والذكريات والتواريخ الخاصة لأحبائك', addDate: 'إضافة تاريخ', editDate: 'تعديل التاريخ', addImportant: 'إضافة تاريخ مهم', dialogDesc: 'احفظ التواريخ المهمة لتغتنم كل فرصة للتعبير عن الحب', recipientName: 'اسم المستلم', recipientPlaceholder: 'مثال: أمي، أحمد، سارة', dateType: 'نوع التاريخ', date: 'التاريخ', month: 'الشهر', day: 'اليوم', year: 'السنة', notes: 'ملاحظات (اختياري)', notesPlaceholder: 'ملاحظات خاصة عن هذا التاريخ...', cancel: 'إلغاء', saving: 'جارٍ الحفظ...', update: 'تحديث', loading: 'جارٍ تحميل التواريخ...', empty: 'لا توجد تواريخ مهمة محفوظة بعد', emptyHint: 'أضف أعياد الميلاد والذكريات لكسب شارات خاصة!', deleteTitle: 'حذف هذا التاريخ؟', deleteBtn: 'حذف', birthday: 'عيد ميلاد', relAnniv: 'ذكرى علاقة', friendAnniv: 'ذكرى صداقة', otherDate: 'تاريخ خاص آخر', months: ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'] },
  ja: { title: '大切な日', desc: '大切な人の誕生日、記念日、特別な日を記録しましょう', addDate: '日付を追加', editDate: '日付を編集', addImportant: '大切な日を追加', dialogDesc: '大切な日を保存して、愛を伝えるチャンスを逃さないように', recipientName: '相手の名前', recipientPlaceholder: '例：お母さん、太郎、花子', dateType: '種類', date: '日付', month: '月', day: '日', year: '年', notes: 'メモ（任意）', notesPlaceholder: 'この日についての特別なメモ...', cancel: 'キャンセル', saving: '保存中...', update: '更新', loading: '日付を読み込み中...', empty: '大切な日がまだ保存されていません', emptyHint: '誕生日や記念日を追加して特別なバッジを獲得しよう！', deleteTitle: 'この日付を削除しますか？', deleteBtn: '削除', birthday: '誕生日', relAnniv: '記念日', friendAnniv: '友情記念日', otherDate: 'その他の特別な日', months: ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'] },
  ko: { title: '중요한 날', desc: '사랑하는 사람의 생일, 기념일, 특별한 날을 기록하세요', addDate: '날짜 추가', editDate: '날짜 수정', addImportant: '중요한 날 추가', dialogDesc: '중요한 날을 저장하여 사랑을 표현할 기회를 놓치지 마세요', recipientName: '수신자 이름', recipientPlaceholder: '예: 엄마, 철수, 영희', dateType: '유형', date: '날짜', month: '월', day: '일', year: '연도', notes: '메모 (선택)', notesPlaceholder: '이 날에 대한 특별한 메모...', cancel: '취소', saving: '저장 중...', update: '수정', loading: '날짜 불러오는 중...', empty: '아직 저장된 중요한 날이 없습니다', emptyHint: '생일과 기념일을 추가하여 특별한 배지를 획득하세요!', deleteTitle: '이 날짜를 삭제하시겠습니까?', deleteBtn: '삭제', birthday: '생일', relAnniv: '연인 기념일', friendAnniv: '우정 기념일', otherDate: '기타 특별한 날', months: ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'] },
  id: { title: 'Tanggal Penting', desc: 'Lacak ulang tahun, hari jadi, dan tanggal istimewa orang tersayang', addDate: 'Tambah Tanggal', editDate: 'Edit Tanggal', addImportant: 'Tambah Tanggal Penting', dialogDesc: 'Simpan tanggal penting agar tidak melewatkan kesempatan mengungkapkan cinta', recipientName: 'Nama Penerima', recipientPlaceholder: 'mis., Ibu, Budi, Sari', dateType: 'Jenis Tanggal', date: 'Tanggal', month: 'Bulan', day: 'Hari', year: 'Tahun', notes: 'Catatan (opsional)', notesPlaceholder: 'Catatan khusus tentang tanggal ini...', cancel: 'Batal', saving: 'Menyimpan...', update: 'Perbarui', loading: 'Memuat tanggal...', empty: 'Belum ada tanggal penting yang tersimpan', emptyHint: 'Tambahkan ulang tahun dan hari jadi untuk mendapatkan lencana khusus!', deleteTitle: 'Hapus tanggal ini?', deleteBtn: 'Hapus', birthday: 'Ulang Tahun', relAnniv: 'Hari Jadi Hubungan', friendAnniv: 'Hari Jadi Pertemanan', otherDate: 'Tanggal Istimewa Lainnya', months: ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'] },
};

type DbDateType = Database['public']['Enums']['date_type'];

const dateTypeIcons: Record<DateType, { icon: typeof Gift; color: string }> = {
  birthday: { icon: Gift, color: 'bg-pink-500/10 text-pink-500' },
  relationship_anniversary: { icon: Heart, color: 'bg-rose-500/10 text-rose-500' },
  friendship_anniversary: { icon: Users, color: 'bg-purple-500/10 text-purple-500' },
  other: { icon: Sparkles, color: 'bg-amber-500/10 text-amber-500' },
};

const dateTypeLabelKeys: Record<DateType, 'birthday' | 'relAnniv' | 'friendAnniv' | 'otherDate'> = {
  birthday: 'birthday',
  relationship_anniversary: 'relAnniv',
  friendship_anniversary: 'friendAnniv',
  other: 'otherDate',
};

interface DateFormData {
  recipient_name: string;
  date_type: DateType;
  date_value: Date | undefined;
  notes: string;
}

const initialFormData: DateFormData = {
  recipient_name: '',
  date_type: 'birthday',
  date_value: undefined,
  notes: '',
};

export function ImportantDatesSection() {
  const { user } = useAuth();
  const { checkDateBadges } = useBadgeAwarder();
  const { showBadgeNudge } = useJacKeyNudgeContext();
  const { language } = useLanguage();
  const dc = lc(datesCopy, language);
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDate, setEditingDate] = useState<ImportantDate | null>(null);
  const [formData, setFormData] = useState<DateFormData>(initialFormData);

  const { data: importantDates = [], isLoading } = useQuery({
    queryKey: ['important-dates', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('important_dates')
        .select('*')
        .eq('user_id', user.id)
        .order('date_value', { ascending: true });
      
      if (error) throw error;
      return data as ImportantDate[];
    },
    enabled: !!user?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (data: DateFormData) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      // Validate input data
      const validationResult = importantDateSchema.safeParse(data);
      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0]?.message || 'Invalid input';
        throw new Error(errorMessage);
      }
      
      const validatedData = validationResult.data;
      
      const { error } = await supabase.from('important_dates').insert({
        user_id: user.id,
        recipient_name: validatedData.recipient_name,
        date_type: validatedData.date_type as DbDateType,
        date_value: format(validatedData.date_value, 'yyyy-MM-dd'),
        notes: validatedData.notes || null,
      });
      
      if (error) throw error;
      return validatedData.date_type;
    },
    onSuccess: async (dateType) => {
      queryClient.invalidateQueries({ queryKey: ['important-dates'] });
      toast.success('Date added successfully!');
      handleCloseDialog();
      
      // Check for date-related badges and show JacKey nudges
      const earnedBadges = await checkDateBadges(dateType as DateType);
      earnedBadges.forEach((badge) => {
        if (badge.badgeKey) {
          showBadgeNudge(badge.badgeKey);
        }
      });
    },
    onError: () => {
      toast.error('Failed to add date');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DateFormData }) => {
      // Validate input data
      const validationResult = importantDateSchema.safeParse(data);
      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0]?.message || 'Invalid input';
        throw new Error(errorMessage);
      }
      
      const validatedData = validationResult.data;
      
      const { error } = await supabase
        .from('important_dates')
        .update({
          recipient_name: validatedData.recipient_name,
          date_type: validatedData.date_type as DbDateType,
          date_value: format(validatedData.date_value, 'yyyy-MM-dd'),
          notes: validatedData.notes || null,
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['important-dates'] });
      toast.success('Date updated successfully!');
      handleCloseDialog();
    },
    onError: () => {
      toast.error('Failed to update date');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('important_dates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['important-dates'] });
      toast.success('Date deleted successfully!');
    },
    onError: () => {
      toast.error('Failed to delete date');
    },
  });

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingDate(null);
    setFormData(initialFormData);
  };

  const handleEdit = (date: ImportantDate) => {
    setEditingDate(date);
    setFormData({
      recipient_name: date.recipient_name,
      date_type: date.date_type,
      date_value: new Date(date.date_value),
      notes: date.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.recipient_name.trim()) {
      toast.error('Please enter a recipient name');
      return;
    }
    if (!formData.date_value) {
      toast.error('Please select a date');
      return;
    }

    if (editingDate) {
      updateMutation.mutate({ id: editingDate.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {dc.title}
            </CardTitle>
            <CardDescription>
              {dc.desc}
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            if (!open) handleCloseDialog();
            else setIsDialogOpen(true);
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                {dc.addDate}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingDate ? dc.editDate : dc.addImportant}</DialogTitle>
                <DialogDescription>
                  {dc.dialogDesc}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="recipient_name">{dc.recipientName}</Label>
                  <Input
                    id="recipient_name"
                    placeholder={dc.recipientPlaceholder}
                    value={formData.recipient_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipient_name: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{dc.dateType}</Label>
                  <Select
                    value={formData.date_type}
                    onValueChange={(value: DateType) => setFormData(prev => ({ ...prev, date_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(dateTypeIcons).map(([key, { icon: Icon }]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {dc[dateTypeLabelKeys[key as DateType]]}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{dc.date}</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.date_value ? String(formData.date_value.getMonth()) : ''}
                      onValueChange={(month) => {
                        const currentDate = formData.date_value || new Date();
                        const newDate = new Date(currentDate.getFullYear(), parseInt(month), currentDate.getDate());
                        setFormData(prev => ({ ...prev, date_value: newDate }));
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={dc.month} />
                      </SelectTrigger>
                      <SelectContent>
                        {dc.months.map((month: string, idx: number) => (
                          <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={formData.date_value ? String(formData.date_value.getDate()) : ''}
                      onValueChange={(day) => {
                        const currentDate = formData.date_value || new Date();
                        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), parseInt(day));
                        setFormData(prev => ({ ...prev, date_value: newDate }));
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue placeholder={dc.day} />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={formData.date_value ? String(formData.date_value.getFullYear()) : ''}
                      onValueChange={(year) => {
                        const currentDate = formData.date_value || new Date();
                        const newDate = new Date(parseInt(year), currentDate.getMonth(), currentDate.getDate());
                        setFormData(prev => ({ ...prev, date_value: newDate }));
                      }}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue placeholder={dc.year} />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">{dc.notes}</Label>
                  <Textarea
                    id="notes"
                    placeholder={dc.notesPlaceholder}
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog}>
                  {dc.cancel}
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? dc.saving : editingDate ? dc.update : dc.addDate}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">{dc.loading}</div>
        ) : importantDates.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">{dc.empty}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {dc.emptyHint}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {importantDates.map((date) => {
              const config = { ...dateTypeIcons[date.date_type], label: dc[dateTypeLabelKeys[date.date_type]] };
              const Icon = config.icon;
              const formattedDate = format(new Date(date.date_value), 'MMMM d');
              
              return (
                <div
                  key={date.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn("p-2 rounded-full", config.color)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium">{date.recipient_name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formattedDate}</span>
                        <Badge variant="secondary" className="text-xs">
                          {config.label}
                        </Badge>
                      </div>
                      {date.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{date.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(date)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{dc.deleteTitle}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {date.recipient_name} — {config.label}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{dc.cancel}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate(date.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {dc.deleteBtn}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
